import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import Modal from '../components/Modal';
import InfoTip from '../components/InfoTip';
import StatTile from '../components/StatTile';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, CopyButton, EmptyState, Field, Icon, PageHeader, Pagination, SearchInput, Skeleton,
  Switch, TableSkeleton, Tabs,
} from '../components/ui';

// ── Events admin ───────────────────────────────────────────────────────────
// Three tabs under one page heading.
//
// Listeners: configured EventBridge listeners (one row per external producer).
// Each listener materializes one custom bus + N rules + bus resource policy
// in AWS. DB is source of truth; "Sync" re-applies the row to AWS.
//
// Event history: every event the interface-external Lambda processed, with
// search, status, detail_type and listener filters, pagination, and totals for
// the last 24 hours and the last 30 days.
//
// Guide: the seven events a producer can send, with their fields and examples.
//
// Detail-types tracked: user.created, user.deleted, user.edited, user.started,
// user.stopped, user.quotas_reset, user.tier_changed.

const NAME_RE = /^[a-z0-9][a-z0-9-]{0,47}$/;
const SOURCE_RE = /^[A-Za-z0-9_.\-/]{1,128}$/;
const ACCOUNT_RE = /^\d{12}$/;

const KNOWN_DETAIL_TYPES = [
  'user.created',
  'user.deleted',
  'user.edited',
  'user.started',
  'user.stopped',
  'user.quotas_reset',
  'user.tier_changed',
];

const EMPTY_DRAFT = {
  name: '',
  source_pattern: '',
  producer_account_id: '',
  description: '',
  enabled: true,
};

const PAGE_SIZE = 10;   // match the Users table page size

// The status filter's options, as the page has offered them. The ifx Lambda writes
// processing, processed, failed and ignored; skipped and pending match nothing it
// writes today, and are kept rather than dropped without a decision.
const STATUS_OPTIONS = ['processed', 'failed', 'ignored', 'skipped', 'pending'];

// Display order of the per-status totals. Anything unlisted sorts last.
const STATUS_ORDER = ['processed', 'success', 'failed', 'error', 'ignored', 'skipped', 'pending'];

const STATUS_TONE = {
  processed: 'ok',
  success: 'ok',
  failed: 'danger',
  error: 'danger',
  ignored: 'neutral',
  skipped: 'neutral',
  pending: 'warn',
  // In-flight — same blue as the user-container "starting" badge.
  processing: 'info',
};

const STATUS_LABEL = {
  processed: 'Processed',
  success: 'Success',
  failed: 'Failed',
  error: 'Error',
  ignored: 'Ignored',
  skipped: 'Skipped',
  pending: 'Pending',
  processing: 'Processing',
};

function statusLabel(status) {
  if (!status) return '—';
  return STATUS_LABEL[status] || status;
}

function EventStatus({ status }) {
  return <Badge tone={STATUS_TONE[status] || 'neutral'} dot>{statusLabel(status)}</Badge>;
}

// Full timestamp, seconds included: the inspect dialog and the cell tooltips.
// UTC throughout, so every operator reads the same clock.
function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { timeZone: 'UTC', hour12: false }) + ' UTC';
  } catch { return iso; }
}

// Short enough for a table cell. The full timestamp rides in the cell's tooltip.
const SHORT_DATE = {
  timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
};

function shortDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', SHORT_DATE) + ' UTC';
  } catch { return iso; }
}

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length > 24 ? `${s.substring(0, 24)}…` : s;
}

function formatCount(n) {
  return typeof n === 'number' ? n.toLocaleString('en-GB') : '—';
}

// One decimal, because the failure rate that matters is the small one: rounded to a
// whole number, 1 failure in 300 reads as "0%".
function share(part, whole) {
  if (!whole || !part) return '0%';
  if (part >= whole) return '100%';
  return `${((part / whole) * 100).toFixed(1)}%`;
}

// One word for a listener's health. `last_error` is set by any failed AWS call
// (create, edit, sync or toggle) and cleared by the next one that succeeds, so it
// outranks the enabled flag: the row may say enabled while AWS holds something else.
function listenerStatus(l) {
  if (l.last_error) return { tone: 'danger', label: 'AWS error' };
  if (!l.enabled) return { tone: 'neutral', label: 'Disabled' };
  return { tone: 'ok', label: 'Active' };
}

// `HelpHint` used to live here: a `?` span with the detail in a native `title`.
// It was the last of that pattern in the app, and it broke the copy standard
// three ways at once — 165 to 210 characters of prose a touch or keyboard user
// could never reach, in an attribute that cannot render the `<code>` these
// strings are mostly made of, on a span that looked like a control but was not
// focusable. The three call sites below use InfoTip instead. InfoTip's panel is
// z-index 1200 against the modal overlay's 1000, so it draws above the dialog
// it sits in.

// ── Listener modal ─────────────────────────────────────────────────────────
function ListenerModal({ mode, initial, onSubmit, onCancel }) {
  const seed = initial
    ? {
        name: initial.name || '',
        source_pattern: initial.source_pattern || '',
        producer_account_id: initial.producer_account_id || '',
        description: initial.description || '',
        enabled: initial.enabled !== false,
      }
    : EMPTY_DRAFT;
  const [draft, setDraft] = useState(seed);
  const [busy, setBusy] = useState(false);
  const isEdit = mode === 'edit';

  const handleSubmit = async () => {
    const name = (draft.name || '').trim().toLowerCase();
    const source = (draft.source_pattern || '').trim();
    const account = (draft.producer_account_id || '').trim();
    if (!isEdit && !NAME_RE.test(name)) {
      toast.error('Name must be 1-48 chars of [a-z0-9-], starting alphanumeric');
      return;
    }
    if (!SOURCE_RE.test(source)) {
      toast.error('Source pattern must be 1-128 chars of [A-Za-z0-9_.-/]');
      return;
    }
    if (account && !ACCOUNT_RE.test(account)) {
      toast.error('Producer account ID must be a 12-digit AWS account number');
      return;
    }
    setBusy(true);
    try {
      const payload = isEdit
        ? {
            source_pattern: source,
            producer_account_id: account || null,
            description: draft.description || null,
            enabled: draft.enabled,
          }
        : {
            name,
            source_pattern: source,
            producer_account_id: account || null,
            description: draft.description || null,
            enabled: draft.enabled,
          };
      await onSubmit(payload);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? undefined : onCancel}
      title={isEdit ? `Edit listener: ${initial.name}` : 'New listener'}
      description={isEdit
        ? 'Saving a change re-applies the listener to AWS.'
        : 'Creating it provisions the AWS event bus and its rules straight away.'}
      maxWidth={600}
    >
      {/* Each field had a HelpHint AND a <small> that restated most of it. The
          hint keeps the one line worth reading without opening anything; the
          tip keeps only what the hint does NOT already say. */}
      <Field
        label="Name"
        htmlFor="eb-listener-name"
        required={!isEdit}
        extra={(
          <InfoTip label="About the listener name" width={340}>
            <p>
              Becomes the suffix of the AWS bus name,{' '}
              <code>{'<project>-<env>-ifx-<name>'}</code>.
            </p>
            <p>
              Lowercase letters, digits and hyphens, 1 to 48 characters, starting
              alphanumeric.
            </p>
          </InfoTip>
        )}
        hint="Names the AWS event bus and rules. Cannot be changed after creation."
      >
        <input
          id="eb-listener-name"
          className="form-input mono"
          value={draft.name}
          disabled={isEdit}
          onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
          placeholder="e-commerce-prod-1"
          spellCheck={false}
          autoComplete="off"
          autoFocus={!isEdit}
        />
      </Field>

      <div className="form-row">
        <Field
          label="Source pattern"
          htmlFor="eb-listener-source"
          required
          extra={(
            <InfoTip label="About the source pattern" width={340}>
              <p>
                This is the rule&apos;s match criterion, so give each producer a
                distinct value: it is what attributes an event in the audit trail.
              </p>
            </InfoTip>
          )}
          hint={<>The value the producer puts in EventBridge&apos;s <code>source</code> field.</>}
        >
          <input
            id="eb-listener-source"
            className="form-input mono"
            value={draft.source_pattern}
            onChange={(e) => setDraft((p) => ({ ...p, source_pattern: e.target.value }))}
            placeholder="nextmindlab.openpaw"
            spellCheck={false}
            autoComplete="off"
            autoFocus={isEdit}
          />
        </Field>

        <Field
          label="Producer AWS account ID"
          htmlFor="eb-listener-account"
          extra={(
            <>
              <span className="text-faint fw-400">(optional)</span>
              <InfoTip label="About the producer account" width={340}>
                <p>
                  Setting this adds an <code>events:PutEvents</code> grant for that
                  account to the bus resource policy. Nothing else is given access.
                </p>
              </InfoTip>
            </>
          )}
          hint={(
            <>
              12-digit AWS account that will <code>PutEvents</code> onto this bus.
              Empty means no cross-account grant.
            </>
          )}
        >
          <input
            id="eb-listener-account"
            className="form-input mono"
            inputMode="numeric"
            value={draft.producer_account_id}
            onChange={(e) => setDraft((p) => ({ ...p, producer_account_id: e.target.value }))}
            placeholder="123456789012"
            spellCheck={false}
            autoComplete="off"
          />
        </Field>
      </div>

      <Field
        label="Description"
        htmlFor="eb-listener-description"
        extra={<span className="text-faint fw-400">(optional)</span>}
      >
        <textarea
          id="eb-listener-description"
          className="form-input"
          rows={2}
          value={draft.description}
          onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
          placeholder="What does this producer represent? Owner, runbook URL, etc."
        />
      </Field>

      <Switch
        checked={!!draft.enabled}
        onChange={(v) => setDraft((p) => ({ ...p, enabled: v }))}
        label="Enabled"
        description={isEdit
          ? "Off disables the listener's rules in AWS when you save."
          : 'Rules are created in the ENABLED state. Off creates them disabled.'}
      />

      <div className="modal-footer sticky">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={handleSubmit}>
          {busy
            ? <><span className="spinner-sm" aria-hidden="true" /> {isEdit ? 'Saving…' : 'Creating…'}</>
            : (isEdit ? 'Save listener' : 'Create listener')}
        </button>
      </div>
    </Modal>
  );
}

// ── Listeners ──────────────────────────────────────────────────────────────
function ListenersCard({ listeners, loading, onRefresh, onOpenCreate, onOpenEdit }) {
  const [actionBusy, setActionBusy] = useState({});
  const [confirm, confirmDialog] = useConfirm();

  const setBusy = (id, action) =>
    setActionBusy((p) => ({ ...p, [id]: action }));

  const handleDelete = async (row) => {
    if (!await confirm({
      title: `Delete listener ${row.name}?`,
      body: (
        <p>
          The AWS event bus and its rules are removed too, so the producer&apos;s
          events stop arriving immediately.
        </p>
      ),
      confirmLabel: 'Delete listener',
    })) return;
    setBusy(row.id, 'delete');
    const data = await callApi(`/api/eventbridge/listeners/${row.id}`, { method: 'DELETE' },
      { successMsg: 'Listener deleted', errorMsg: 'Delete failed' });
    setBusy(row.id, null);
    if (data) onRefresh();
  };

  const handleToggle = async (row) => {
    setBusy(row.id, row.enabled ? 'disable' : 'enable');
    const data = await callApi(`/api/eventbridge/listeners/${row.id}/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !row.enabled }),
    }, { successMsg: `Listener ${row.enabled ? 'disabled' : 'enabled'}`, errorMsg: 'Toggle failed' });
    setBusy(row.id, null);
    if (data) onRefresh();
  };

  const handleSync = async (row) => {
    setBusy(row.id, 'sync');
    const data = await callApi(`/api/eventbridge/listeners/${row.id}/sync`, { method: 'POST' },
      { successMsg: 'Synced with AWS', errorMsg: 'Sync failed' });
    setBusy(row.id, null);
    if (data) onRefresh();
  };

  return (
    <div className="card card-flush">
      <div className="card-header">
        <div>
          <h2 className="card-title">
            Listeners
            <InfoTip label="About listeners" width={390}>
              <p>
                One row per producer. Creating it provisions an AWS event bus and a rule for each
                of the seven <code>user.*</code> events, all pointing at the handler Lambda that
                calls the platform.
              </p>
              <p>
                The producer then sends events to that bus from the AWS account configured on the
                row. Nothing else is granted access to it.
              </p>
              <p>
                <strong>Sync</strong> re-applies the row to AWS. Use it if someone may have
                changed the bus or rules outside this page.
              </p>
              <p>
                Events are one of two ways to drive the same operations, a producer that wants a
                synchronous answer can call the equivalent endpoints with an API key instead.
                Both run the same code.
              </p>
            </InfoTip>
          </h2>
          <p className="card-subtitle">
            Each row wires one producer&apos;s AWS account to this platform, so its user
            events reach us.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={onOpenCreate}>
          <Icon name="plus" /> New listener
        </button>
      </div>

      {loading ? (
        <TableSkeleton rows={3} cols={7} label="Loading listeners" />
      ) : listeners.length === 0 ? (
        <EmptyState
          icon="events"
          title="No listeners yet"
          action={(
            <button type="button" className="btn btn-primary" onClick={onOpenCreate}>
              Create the first listener
            </button>
          )}
        >
          A listener gives one producer&apos;s AWS account an event bus to send user events
          to. Nothing arrives until one exists.
        </EmptyState>
      ) : (
        <div className="table-container">
          <table className="table table-stack">
            <thead>
              <tr>
                <th>Listener</th>
                <th>Source pattern</th>
                <th>Producer account</th>
                <th>Status</th>
                <th>Last sync</th>
                <th>Enabled</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {listeners.map((l) => {
                const busy = actionBusy[l.id];
                const status = listenerStatus(l);
                const switchId = `eb-listener-enabled-${l.id}`;
                return (
                  <tr key={l.id}>
                    <td data-label="Listener">
                      {/* max-width on a <td> is ignored by table layout; on a block inside
                          it, it holds, so a long description wraps instead of widening
                          the column. */}
                      <div style={{ maxWidth: 260, minWidth: 0 }}>
                        <span className="cell-primary">{l.name}</span>
                        {l.description && <span className="cell-sub">{l.description}</span>}
                      </div>
                    </td>
                    <td data-label="Source pattern"><code>{l.source_pattern}</code></td>
                    <td data-label="Producer account">
                      {l.producer_account_id
                        ? <code>{l.producer_account_id}</code>
                        : <span className="text-faint">Not set</span>}
                    </td>
                    <td data-label="Status">
                      <Badge tone={status.tone} dot>{status.label}</Badge>
                    </td>
                    <td data-label="Last sync">
                      <div className="stack gap-1 items-start" style={{ maxWidth: 260, minWidth: 0 }}>
                        <span className="num nowrap" title={l.last_synced_at ? formatDate(l.last_synced_at) : undefined}>
                          {l.last_synced_at ? shortDate(l.last_synced_at) : 'Never'}
                        </span>
                        {/* The error used to exist only in a native tooltip on a truncated
                            line, so a keyboard or touch user could not read why a sync
                            failed. The line stays for a glance, the tip holds all of it. */}
                        {l.last_error && (
                          <span className="row gap-1 w-full text-sm text-danger">
                            <span className="truncate">{l.last_error}</span>
                            <InfoTip label={`Last AWS error for ${l.name}`} width={380}>
                              <p className="break">{l.last_error}</p>
                            </InfoTip>
                          </span>
                        )}
                      </div>
                    </td>
                    <td data-label="Enabled">
                      <div className="row gap-2">
                        <label htmlFor={switchId} className="sr-only">Enable listener {l.name}</label>
                        <Switch
                          id={switchId}
                          checked={l.enabled}
                          disabled={!!busy}
                          onChange={() => handleToggle(l)}
                        />
                        {(busy === 'enable' || busy === 'disable') && (
                          <span className="spinner-sm text-muted" aria-hidden="true" />
                        )}
                      </div>
                    </td>
                    <td data-label="Actions">
                      <div className="row gap-1 row-end">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={!!busy}
                          onClick={() => handleSync(l)}
                          aria-label={`Sync ${l.name} with AWS`}
                        >
                          {busy === 'sync'
                            ? <span className="spinner-sm" aria-hidden="true" />
                            : <Icon name="refresh" />}
                          Sync
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm btn-icon"
                          disabled={!!busy}
                          onClick={() => onOpenEdit(l)}
                          aria-label={`Edit listener ${l.name}`}
                          title="Edit listener"
                        >
                          <Icon name="edit" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger-soft btn-sm btn-icon"
                          disabled={!!busy}
                          onClick={() => handleDelete(l)}
                          aria-label={`Delete listener ${l.name}`}
                          title="Delete listener"
                        >
                          {busy === 'delete'
                            ? <span className="spinner-sm" aria-hidden="true" />
                            : <Icon name="trash" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirmDialog}
    </div>
  );
}

// ── Totals ─────────────────────────────────────────────────────────────────
// Two windows from one call. `last_24h` is its own rollup ({ total, failed });
// `by_type` is 30 days of (detail_type, status) counts. The banner these tiles
// replace summed `by_type` under a "Last 24h" label, so it reported a month as
// a day, and nothing read `last_24h` at all.
function StatsTiles({ stats }) {
  if (!stats || !Array.isArray(stats.by_type)) return null;

  // Aggregate counts by status for the 30-day window.
  const totals = stats.by_type.reduce((acc, row) => {
    const s = row.status || 'unknown';
    acc[s] = (acc[s] || 0) + (row.count || 0);
    return acc;
  }, {});
  const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);
  const failedMonth = (totals.failed || 0) + (totals.error || 0);
  const day = stats.last_24h || {};
  // A response without the rollup shows a dash, not a confident "Nothing received".
  const dayKnown = typeof day.total === 'number';

  const entries = Object.entries(totals).sort((a, b) => {
    const ia = STATUS_ORDER.indexOf(a[0]); const ib = STATUS_ORDER.indexOf(b[0]);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  return (
    <div className="dashboard-grid">
      <StatTile
        span={3}
        label="Last 24 hours"
        value={formatCount(day.total)}
        sub="events received"
        help="Both windows count an event by when the platform processed it, not by the occurred_at the producer sent."
      />
      <StatTile
        span={3}
        label="Failed, last 24 hours"
        value={formatCount(day.failed)}
        tone={day.failed > 0 ? 'negative' : undefined}
        sub={!dayKnown ? undefined
          : day.total > 0 ? `${share(day.failed, day.total)} of received` : 'Nothing received'}
      />
      <StatTile
        span={3}
        label="Last 30 days"
        value={formatCount(grandTotal)}
        sub={grandTotal === 0 ? 'No events received in the last 30 days.' : (
          <span className="row-wrap gap-1">
            {entries.map(([status, n]) => (
              <Badge key={status} tone={STATUS_TONE[status] || 'neutral'}>
                {statusLabel(status)}: {formatCount(n)}
              </Badge>
            ))}
          </span>
        )}
      />
      <StatTile
        span={3}
        label="Failed, last 30 days"
        value={formatCount(failedMonth)}
        tone={failedMonth > 0 ? 'negative' : undefined}
        sub={grandTotal > 0 ? `${share(failedMonth, grandTotal)} of received` : 'Nothing received'}
      />
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="dashboard-grid" role="status" aria-busy="true">
      <span className="sr-only">Loading event totals</span>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="card dashboard-card kpi-tile dashboard-tile-3" aria-hidden="true">
          <Skeleton width="48%" height={10} />
          <Skeleton width="28%" height={26} />
          <Skeleton width="62%" height={10} />
        </div>
      ))}
    </div>
  );
}

// ── Event inspect modal ────────────────────────────────────────────────────
function EventInspectModal({ event, onClose }) {
  const raw = event && event.raw_detail
    ? (typeof event.raw_detail === 'string'
        ? event.raw_detail
        : JSON.stringify(event.raw_detail, null, 2))
    : null;

  return (
    <Modal
      open={!!event}
      onClose={onClose}
      maxWidth={760}
      title={event ? (event.detail_type || 'Event') : ''}
      description={event ? <span className="mono break">{event.event_id || '—'}</span> : null}
    >
      {event && (
        <>
          <dl className="kv">
            <dt>Status</dt>
            <dd><EventStatus status={event.status} /></dd>
            <dt>Listener</dt>
            <dd>{event.listener_name || event.listener_id || <span className="text-faint">None</span>}</dd>
            <dt>Detail type</dt>
            <dd className="mono">{event.detail_type || '—'}</dd>
            <dt>Message</dt>
            <dd className="pre-wrap">{event.message || '—'}</dd>
            <dt>User</dt>
            <dd>{event.username || event.user_id || '—'}</dd>
            <dt>Occurred at</dt>
            <dd className="num">{formatDate(event.occurred_at)}</dd>
            <dt>Processed at</dt>
            <dd className="num">{formatDate(event.processed_at)}</dd>
          </dl>

          <div className="subhead mt-5">
            <h4 className="subhead-title">Raw detail</h4>
            {raw && <CopyButton value={raw} label="Copy raw detail" />}
          </div>
          {raw ? (
            <pre className="code-block pre-wrap m-0" style={{ maxHeight: 360, overflow: 'auto' }}>{raw}</pre>
          ) : (
            <p className="text-muted">No payload available.</p>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── Event history ──────────────────────────────────────────────────────────
function EventsCard({ listeners }) {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailTypeFilter, setDetailTypeFilter] = useState('');
  const [listenerFilter, setListenerFilter] = useState('');
  const [serverDetailTypes, setServerDetailTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [inspectEvent, setInspectEvent] = useState(null);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [statusFilter, detailTypeFilter, listenerFilter]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
      if (searchDebounced) params.set('search', searchDebounced);
      if (statusFilter) params.set('status', statusFilter);
      if (detailTypeFilter) params.set('detail_type', detailTypeFilter);
      if (listenerFilter) params.set('listener_id', listenerFilter);
      const resp = await apiFetch(`/api/eventbridge/events?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setEvents(data.events || []);
        setTotal(data.total || 0);
        if (Array.isArray(data.detail_types)) setServerDetailTypes(data.detail_types);
      } else {
        toast.error('Failed to load events');
      }
    } catch {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [page, searchDebounced, statusFilter, detailTypeFilter, listenerFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const resp = await apiFetch('/api/eventbridge/events/stats');
      if (resp.ok) {
        const data = await resp.json();
        setStats(data);
      }
    } catch {
      // non-fatal — the totals just won't render
    } finally {
      setStatsLoaded(true);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const detailTypeOptions = serverDetailTypes.length > 0 ? serverDetailTypes : KNOWN_DETAIL_TYPES;

  const handleRefresh = () => { fetchEvents(); fetchStats(); };

  const hasFilters = !!(search || statusFilter || detailTypeFilter || listenerFilter);
  // What the list on screen was actually fetched with: the search box runs 300ms ahead.
  const filtered = !!(searchDebounced || statusFilter || detailTypeFilter || listenerFilter);
  const clearFilters = () => {
    setSearch(''); setStatusFilter('');
    setDetailTypeFilter(''); setListenerFilter('');
  };

  return (
    <>
      {statsLoaded ? <StatsTiles stats={stats} /> : <StatsSkeleton />}

      <div className="card card-flush">
        <div className="card-header">
          <div>
            <h2 className="card-title">
              Event history
              <InfoTip label="About the event history" width={380}>
                <p>
                  Every event the platform has received, with the outcome. A <code>failed</code> row
                  carries the backend's response, which is the first place to look when a producer
                  reports that something did not happen.
                </p>
                <p>
                  Events are de-duplicated: a producer may safely resend one. A repeat of an event
                  already recorded is skipped rather than applied twice.
                </p>
                <p>
                  Rows are pruned on the retention window set under Data Governance, so this is
                  recent history rather than a permanent ledger.
                </p>
              </InfoTip>
            </h2>
            <p className="card-subtitle">
              What each producer has sent, and whether it was applied.
            </p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={handleRefresh}>
            {loading ? <span className="spinner-sm" aria-hidden="true" /> : <Icon name="refresh" />}
            Refresh
          </button>
        </div>

        <div className="toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search user, message, event ID…"
            label="Search events"
          />
          <select
            className="form-input"
            aria-label="Filter by status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{statusLabel(s)}</option>
            ))}
          </select>
          <select
            className="form-input"
            aria-label="Filter by detail type"
            value={detailTypeFilter}
            onChange={(e) => setDetailTypeFilter(e.target.value)}
          >
            <option value="">All detail types</option>
            {detailTypeOptions.map((dt) => (
              <option key={dt} value={dt}>{dt}</option>
            ))}
          </select>
          <select
            className="form-input"
            aria-label="Filter by listener"
            value={listenerFilter}
            onChange={(e) => setListenerFilter(e.target.value)}
          >
            <option value="">All listeners</option>
            {listeners.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          {hasFilters && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters}>
              <Icon name="x" /> Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <TableSkeleton rows={PAGE_SIZE} cols={7} label="Loading events" />
        ) : events.length === 0 && !filtered && total === 0 ? (
          <EmptyState icon="events" title="No events yet">
            Every event a producer sends to a listener&apos;s bus is recorded here, with its outcome.
          </EmptyState>
        ) : events.length === 0 ? (
          <>
            <EmptyState
              icon="search"
              title="No events match the current filters"
              action={filtered
                ? <button type="button" className="btn btn-secondary" onClick={clearFilters}>Clear filters</button>
                : null}
            >
              Search covers the user, message, event ID, detail type, status and listener name.
            </EmptyState>
            {/* Kept under an empty page too, as before: a page emptied by pruning
                between two loads is otherwise a dead end. */}
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} noun="events" />
          </>
        ) : (
          <>
            <div className="table-container">
              <table className="table table-stack">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Status</th>
                    <th>Listener</th>
                    <th>User</th>
                    <th>Message</th>
                    <th>Occurred</th>
                    <th><span className="sr-only">Inspect</span></th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr
                      key={ev.event_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setInspectEvent(ev)}
                    >
                      <td data-label="Event">
                        {/* One block, so the stacked phone layout keeps the id under the type
                            instead of spreading the two across the row. */}
                        <div className="min-w-0">
                          <code className="nowrap">{ev.detail_type || '—'}</code>
                          <span className="cell-sub mono nowrap">{shortId(ev.event_id)}</span>
                        </div>
                      </td>
                      <td data-label="Status"><EventStatus status={ev.status} /></td>
                      <td data-label="Listener">
                        {ev.listener_name || ev.listener_id || <span className="text-faint">None</span>}
                      </td>
                      <td data-label="User">
                        {ev.username
                          ? <span>{ev.username}</span>
                          : ev.user_id
                            ? <code>{ev.user_id}</code>
                            : <span className="text-faint">—</span>}
                      </td>
                      <td data-label="Message">
                        <div className="truncate text-sm text-muted" style={{ maxWidth: 280 }} title={ev.message || undefined}>
                          {ev.message || ''}
                        </div>
                      </td>
                      <td data-label="Occurred" className="nowrap">
                        <span className="num text-sm text-muted" title={ev.occurred_at ? formatDate(ev.occurred_at) : undefined}>
                          {shortDate(ev.occurred_at)}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm btn-icon"
                          onClick={() => setInspectEvent(ev)}
                          aria-label={`Inspect event ${ev.detail_type || ''} ${shortId(ev.event_id)}`}
                          title="Inspect"
                        >
                          <Icon name="eye" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} noun="events" />
          </>
        )}
      </div>

      <EventInspectModal event={inspectEvent} onClose={() => setInspectEvent(null)} />
    </>
  );
}

// ── Page root ──────────────────────────────────────────────────────────────
export default function Eventbridge() {
  const [listeners, setListeners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState('listeners');

  const fetchListeners = useCallback(async () => {
    try {
      const resp = await apiFetch('/api/eventbridge/listeners');
      if (resp.ok) {
        const data = await resp.json();
        setListeners(Array.isArray(data) ? data : (data.listeners || []));
      } else {
        toast.error('Failed to load listeners');
      }
    } catch {
      toast.error('Failed to load listeners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchListeners(); }, [fetchListeners]);

  const handleCreate = async (payload) => {
    const data = await callApi('/api/eventbridge/listeners', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }, { successMsg: 'Listener created + pushed to AWS', errorMsg: 'Create failed' });
    if (data) {
      setModalMode(null);
      setEditing(null);
      fetchListeners();
    }
  };

  const handleEdit = async (payload) => {
    if (!editing) return;
    const data = await callApi(`/api/eventbridge/listeners/${editing.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }, { successMsg: 'Listener updated', errorMsg: 'Update failed' });
    if (data) {
      setModalMode(null);
      setEditing(null);
      fetchListeners();
    }
  };

  const tabs = [
    { value: 'listeners', label: 'Listeners', icon: 'events', count: loading ? undefined : listeners.length },
    { value: 'events', label: 'Event history', icon: 'history' },
    { value: 'guide', label: 'Guide', icon: 'fileText' },
  ];

  return (
    <div>
      {/* The heading belongs HERE, not in ListenersCard where it was first put:
          that renders only on the listeners tab, so the other two tabs had no
          heading and this one showed an <h1> below the tab strip. */}
      <PageHeader description="What a producer can send us, what arrived, and which listeners are wired.">
        <h1 className="page-title">Events</h1>
      </PageHeader>

      <div className="page-tabs">
        <Tabs tabs={tabs} value={tab} onChange={setTab} variant="line" label="Events sections" idPrefix="eb" />
      </div>

      {tab === 'listeners' && (
        <div role="tabpanel" id="eb-panel-listeners" aria-labelledby="eb-tab-listeners">
          <ListenersCard
            listeners={listeners}
            loading={loading}
            onRefresh={fetchListeners}
            onOpenCreate={() => { setEditing(null); setModalMode('create'); }}
            onOpenEdit={(row) => { setEditing(row); setModalMode('edit'); }}
          />
        </div>
      )}

      {tab === 'events' && (
        <div role="tabpanel" id="eb-panel-events" aria-labelledby="eb-tab-events">
          <EventsCard listeners={listeners} />
        </div>
      )}

      {tab === 'guide' && (
        <div role="tabpanel" id="eb-panel-guide" aria-labelledby="eb-tab-guide">
          <GuideCard />
        </div>
      )}

      {modalMode && (
        <ListenerModal
          mode={modalMode}
          initial={editing}
          onSubmit={modalMode === 'edit' ? handleEdit : handleCreate}
          onCancel={() => { setModalMode(null); setEditing(null); }}
        />
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Guide tab — operator documentation for the 7 events + producer setup.
// Pure presentation; no API calls. Update the EVENT_DOCS array when
// detail-types are added/changed on the backend.
// ──────────────────────────────────────────────────────────────────────

const _IFX_FIELD = {
  name: 'interface_external_user_id',
  type: 'string',
  required: true,
  desc: 'Producer-supplied stable opaque id. The lookup key (path param) on every event after user.created, never changes.',
};

const EVENT_DOCS = [
  {
    type: 'user.created',
    method: 'POST',
    endpoint: '/internal/users',
    when: 'The producer provisions a new user (e.g. right after a successful checkout).',
    summary: 'Creates the local user, auto-assigns a Telegram bot from the pool, mints the activation token, and provisions the EFS workspace. Idempotent on interface_external_user_id: replaying returns the existing row (created=false).',
    fields: [
      _IFX_FIELD,
      { name: 'username', type: 'string', required: true, desc: 'Unique name of the account; may be the email address. 2-100 characters: letters, digits and . _ + @ -, starting and ending with a letter or digit. Stored lowercased. 400 on mismatch, or when it ends in a hyphen and 8 hex characters or contains _deleted_; 409 when taken.' },
      { name: 'email', type: 'string', required: true, desc: 'User email address.' },
      { name: 'first_name', type: 'string', required: false, desc: 'Display first name.' },
      { name: 'last_name', type: 'string', required: false, desc: 'Display last name.' },
      { name: 'tier_sku', type: 'string', required: false, desc: 'Resolves to tiers.sku (case-insensitive tiers.name fallback). Defaults to the tier flagged is_default.' },
      { name: 'subscription_period_end', type: 'string | epoch', required: false, desc: 'ISO-8601 (2026-07-08T00:00:00Z) or Unix epoch seconds. Stored on users.subscription_period_end, the /piano "valid until" date.' },
    ],
    detail: {
      interface_external_user_id: 'ext_abc123',
      username: 'alice123',
      email: 'alice@example.com',
      first_name: 'Alice',
      last_name: 'Rossi',
      tier_sku: 'pro',
      subscription_period_end: '2026-07-08T00:00:00Z',
    },
    returns: '{ success, user_id, external_id, username, activation_token, container_name, telegram_bot_username, created }',
  },
  {
    type: 'user.deleted',
    method: 'DELETE',
    endpoint: '/internal/users/{external_id}',
    when: 'The producer removes a user (account closed / cancelled).',
    summary: 'Soft-delete: sets deleted_at, stops the agent, and frees the workspace. The row is kept (data retained, recoverable by an admin). Permanent erase stays an explicit admin-only action.',
    fields: [_IFX_FIELD],
    detail: { interface_external_user_id: 'ext_abc123' },
    returns: '{ success, external_id, user_id, result }',
  },
  {
    type: 'user.edited',
    method: 'PATCH',
    endpoint: '/internal/users/{external_id}',
    when: 'A profile field changed upstream (name / email / display username).',
    summary: 'Sparse patch: only the whitelisted fields present in the body are written. Fields outside the whitelist are silently dropped. Tier changes go through user.tier_changed, not here.',
    fields: [
      _IFX_FIELD,
      { name: 'email', type: 'string', required: false, desc: 'New email address.' },
      { name: 'first_name', type: 'string', required: false, desc: 'New first name.' },
      { name: 'last_name', type: 'string', required: false, desc: 'New last name.' },
      { name: 'username', type: 'string', required: false, desc: 'New username, same rule as user.created (400 otherwise, 409 when taken). The cross-event key stays interface_external_user_id. The agent\'s Kubernetes objects follow the current username, so rename while the agent is stopped or hibernated.' },
    ],
    detail: { interface_external_user_id: 'ext_abc123', email: 'new@example.com', first_name: 'Alice' },
    returns: '{ success, external_id, user_id, updated: […] }',
  },
  {
    type: 'user.started',
    method: 'POST',
    endpoint: '/internal/users/{external_id}/start',
    when: 'Re-activate a suspended user. Send this on every resubscription: it is the ONLY event that makes an account live again.',
    summary: "Marks the subscription active and moves a stopped agent to hibernated. Does NOT boot a pod: the agent comes up on the user's first message, or ahead of a scheduled cron. Workspace resumes from where user.stopped left it. Idempotent on an already-active user.",
    fields: [_IFX_FIELD],
    detail: { interface_external_user_id: 'ext_abc123' },
    returns: '{ success, external_id, user_id, activated, resumed, noop }',
  },
  {
    type: 'user.stopped',
    method: 'POST',
    endpoint: '/internal/users/{external_id}/stop',
    when: 'Suspend a user without deleting their data (e.g. payment lapsed).',
    summary: 'Stops the agent and marks the subscription inactive, keeping the workspace and the row. Cheaper steady state than user.deleted. Nothing but user.started undoes it: a later user.quotas_reset alone leaves the account inactive and unreachable.',
    fields: [_IFX_FIELD],
    detail: { interface_external_user_id: 'ext_abc123' },
    returns: '{ success, external_id, user_id, result }',
  },
  {
    type: 'user.quotas_reset',
    method: 'POST',
    endpoint: '/internal/users/{external_id}/reset-quota',
    when: 'A new billing window opens (subscription renewal).',
    summary: 'Zeroes all three spend counters (period_cost_usd, period_tokens_used, backup_period_cost_usd) and moves billing_period_start to now, in one statement. Optionally bumps the subscription period end on renewal.',
    fields: [
      _IFX_FIELD,
      { name: 'subscription_period_end', type: 'string | epoch', required: false, desc: 'ISO-8601 or Unix epoch. Updates the /piano "valid until" date for the new period. Omit to leave it unchanged.' },
    ],
    detail: { interface_external_user_id: 'ext_abc123', subscription_period_end: '2026-08-08T00:00:00Z' },
    returns: '{ success, external_id, user_id, reset, subscription_period_end }',
  },
  {
    type: 'user.tier_changed',
    method: 'POST',
    endpoint: '/internal/users/{external_id}/tier',
    when: 'The user moves to a different subscription tier (up/downgrade).',
    summary: 'Re-points users.tier_id. tier_sku is resolved against tiers.sku (case-insensitive tiers.name fallback for legacy schemas). 404 if the sku is unknown.',
    fields: [
      _IFX_FIELD,
      { name: 'tier_sku', type: 'string', required: true, desc: 'Target tier sku (or name). Must resolve to an existing tier.' },
    ],
    detail: { interface_external_user_id: 'ext_abc123', tier_sku: 'pro' },
    returns: '{ success, external_id, user_id, tier_id, tier_name, tier_sku }',
  },
];

const METHOD_TONE = { POST: 'ok', DELETE: 'danger', PATCH: 'warn', GET: 'info' };

function MethodBadge({ method }) {
  return <Badge tone={METHOD_TONE[method] || 'neutral'} square>{method}</Badge>;
}

// Per-event "swagger" modal: method + endpoint, when it fires, the field
// spec table, an example detail payload, and a copy-paste EventBridge entry.
function EventDetailModal({ doc, onClose }) {
  if (!doc) return null;
  const pretty = JSON.stringify(doc.detail, null, 2);
  const entry =
    `aws events put-events --entries '[{\n` +
    `  "EventBusName": "arn:aws:events:eu-central-1:088070740348:event-bus/<your-bus>",\n` +
    `  "Source": "<your source_pattern>",\n` +
    `  "DetailType": "${doc.type}",\n` +
    `  "Detail": ${JSON.stringify(JSON.stringify(doc.detail))}\n` +
    `}]'`;
  const copy = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(
        () => toast.success('Copied'),
        () => toast.error('Copy failed'),
      );
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      maxWidth={720}
      title={doc.type}
      description={(
        <span className="row-wrap gap-2">
          <MethodBadge method={doc.method} />
          <code>{doc.endpoint}</code>
        </span>
      )}
    >
      <p><strong>When it fires.</strong> {doc.when}</p>
      <p>{doc.summary}</p>

      <div className="subhead mt-5">
        <h4 className="subhead-title">Detail fields</h4>
      </div>
      <div className="table-wrap bordered">
        <table className="table table-compact table-stack">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Required</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {doc.fields.map((f) => (
              <tr key={f.name}>
                <td data-label="Field"><code>{f.name}</code></td>
                <td data-label="Type" className="text-muted nowrap">{f.type}</td>
                <td data-label="Required">
                  {f.required
                    ? <Badge tone="danger">Required</Badge>
                    : <span className="text-faint">Optional</span>}
                </td>
                <td data-label="Description" className="wrap text-2" style={{ minWidth: 220 }}>{f.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="subhead mt-5">
        <h4 className="subhead-title">Example detail payload</h4>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => copy(pretty)}
                aria-label="Copy example payload">
          <Icon name="copy" /> Copy
        </button>
      </div>
      <pre className="code-block pre-wrap m-0">{pretty}</pre>

      <div className="subhead mt-5">
        <h4 className="subhead-title">Full EventBridge entry</h4>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => copy(entry)}
                aria-label="Copy EventBridge entry">
          <Icon name="copy" /> Copy
        </button>
      </div>
      <pre className="code-block pre-wrap m-0">{entry}</pre>

      {doc.returns && (
        <p className="mt-4 text-sm">
          <strong>Backend response.</strong>{' '}
          <code>{doc.returns}</code>
        </p>
      )}

      <p className="mt-3 text-sm text-muted">
        Every event also accepts an optional <code>event_id</code> (dedup key, a replay with the
        same id is skipped; falls back to the EventBridge envelope id) and <code>occurred_at</code>.
        On dispatch failure the ledger row is marked <code>failed</code> with the backend response
        body in <code>message</code>.
      </p>

      <div className="modal-footer sticky">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

function GuideCard() {
  const [openDoc, setOpenDoc] = useState(null);
  return (
    <div className="card card-flush">
      <div className="card-header">
        <div>
          <h2 className="card-title">
            Events a producer can send
            <InfoTip label="About the event reference" width={390}>
              <p>
                Every user is addressed by <code>interface_external_user_id</code>, an id the
                producer chooses and sends on <code>user.created</code>, then reuses on every
                later event for that user.
              </p>
              <p>
                Supplying it is what makes creation safe to retry: sending the same id twice
                returns the existing account instead of making a second one.
              </p>
              <p>
                Each of these has an equivalent API endpoint for a producer that needs an answer
                in the response rather than fire-and-forget. Same behaviour either way.
              </p>
            </InfoTip>
          </h2>
          <p className="card-subtitle">
            The seven events this platform accepts. Open <em>Details</em> for a field
            list and an example payload.
          </p>
        </div>
      </div>
      <div className="table-container">
        <table className="table table-stack">
          <thead>
            <tr>
              <th>Detail type</th>
              <th>When the producer fires it</th>
              <th>Backend endpoint</th>
              <th><span className="sr-only">Details</span></th>
            </tr>
          </thead>
          <tbody>
            {EVENT_DOCS.map((d) => (
              <tr key={d.type}>
                <td data-label="Detail type"><code className="nowrap">{d.type}</code></td>
                <td data-label="When" className="text-2" style={{ minWidth: 240 }}>{d.when}</td>
                <td data-label="Endpoint">
                  <span className="row gap-2 nowrap">
                    <MethodBadge method={d.method} />
                    <code>{d.endpoint}</code>
                  </span>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setOpenDoc(d)}
                    aria-label={`Details for ${d.type}`}
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EventDetailModal doc={openDoc} onClose={() => setOpenDoc(null)} />
    </div>
  );
}
